// BRK-S164 — how much of the cap table is on the request, as one bar.
//
// ⚠️ THIS REPLACED THE YELLOW "WHO HAS TO SIGN" BOX (`BondSignerChecklist`,
// BRK-S152/S156), and the replacement is a narrowing on purpose.
//
// The box did four jobs at once: it stated the rule, it drew this bar, it
// listed every seat with a status pill, and it explained why the footer's
// Continue was dead. Aaron's 2026-09-21 walk is what that costs — an amber
// card of prose above a stack of owner blocks, saying things ABOUT people
// whose cards are somewhere below it, with a "Go to this owner" jump between
// the two. The seats moved onto the cards they are about
// (`rosterCardStatus`), the gate copy went with the disabled button, and what
// is left here is the one fact no card can carry: the roster as a whole
// against the rule's line.
//
// It renders the ORDER's own evaluation and decides nothing — the first rule
// of `bondRoster.ts`, unchanged.
import {
  INDEMNITY_PROGRESS_LABEL,
  indemnityProgress,
  ownersIncludedCopy,
} from "@/lib/bondRoster";
import type { BondIndemnity } from "@/lib/brokerBonds";

export function BondOwnershipProgress({
  indemnity,
}: {
  indemnity: BondIndemnity;
}) {
  const progress = indemnityProgress(indemnity);
  return (
    <section
      aria-labelledby="ownership-progress-heading"
      // The reading changes when the SERVER re-evaluates it, which is not a
      // change the broker made on the control under their cursor — so it is
      // announced rather than left to be noticed.
      aria-live="polite"
      className="flex flex-col gap-1.5"
    >
      {/* No bar at all for a rule with no line (`representedShare: null`):
          filling toward a target that does not exist would invent one, and the
          minimum-share sentence below is that product's whole rule. The
          HEADING stays, because the section still has something to say. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
        <span
          id="ownership-progress-heading"
          className="text-sm font-medium text-brk-text"
        >
          {INDEMNITY_PROGRESS_LABEL}
        </span>
        {progress && (
          <span className="text-sm text-brk-dim">{progress.label}</span>
        )}
      </div>
      {progress && (
        <div
          role="progressbar"
          aria-label={INDEMNITY_PROGRESS_LABEL}
          // What it ANNOUNCES is the SHARE, not the fill: `percent` measures
          // distance to the line, and reading 60% to somebody whose cap table
          // says 30 would be two numbers for one fact.
          aria-valuemin={0}
          aria-valuemax={progress.line}
          aria-valuenow={progress.listed}
          aria-valuetext={progress.label}
          className="h-2 w-full overflow-hidden rounded-full bg-brk-border"
        >
          <div
            data-testid="ownership-progress-fill"
            // `met` is EXCLUSIVE (`>`), Product's own word — 50 of 50 is a
            // full bar that is not met, so the colour and the width are two
            // different facts and neither is derived from the other.
            data-met={progress.met ? "true" : "false"}
            className={`h-full rounded-full transition-[width] ${
              progress.met ? "bg-emerald-500" : "bg-amber-500"
            }`}
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      )}
      <p className="text-sm text-brk-dim">
        {ownersIncludedCopy(indemnity.rule)}
      </p>
    </section>
  );
}
